import React from 'react';
import useBrandContext from 'contexts/brand.context';
import BrandStatus from 'components/brand-status';
import BrandDetailsCard from 'components/brand-details-card';
import BrandImageDetailCard from 'components/brand-image-detail-card';
import LiveTag from 'components/live-tag';
import { Input } from '@cokitchen/cokitchen-components';
import { ALL_BRAND_TYPE_OPTIONS, FC_BRAND_TYPE_OPTIONS } from 'utils/constants';
import { isFCBBrand } from 'utils/functions';
import moment from 'moment';

type BrandTypeCheckbox = {
  label: string;
  checked: boolean;
  name: string;
};

const PreviewBrandDetails = () => {
  const { activeBrand, companyOptions, setActiveBrand } = useBrandContext();

  const selectedCompanies = (activeBrand?.company_brands?.map((b) => b.company.id) ?? [])
    .map((c) => companyOptions.find((o) => o.value === c)?.text)
    .filter(Boolean);

  const brandTypes: BrandTypeCheckbox[] = [
    {
      label: 'Pre Order',
      checked: activeBrand?.preorder,
      name: 'preorder'
    },
    {
      label: 'Private',
      checked: activeBrand?.is_private ?? false,
      name: 'is_private'
    },

    {
      label: 'FCB',
      checked: isFCBBrand(activeBrand.brand_type),
      name: 'is_fcb'
    },
    {
      label: 'Foodcourt',
      checked: FC_BRAND_TYPE_OPTIONS.some((opt) => opt.value === activeBrand.brand_type),
      name: 'foodcourt'
    }
  ];

  return (
    <div>
      <BrandDetailsCard data={{ label: 'Brand Name', value: activeBrand?.name }} className='my-4' />
      <BrandDetailsCard data={{ label: 'Brand Slug', value: activeBrand?.slug }} className='my-4' />
      <BrandDetailsCard
        data={{ label: 'Brand Tagline', value: activeBrand?.short_text }}
        className='my-4'
      />
      <BrandDetailsCard
        data={{ label: 'Brand Summary', value: activeBrand?.summary }}
        className='my-4'
      />
      <BrandDetailsCard
        data={{ label: 'Brand Description', value: activeBrand?.description }}
        className='my-4'
      />

      <fieldset className='flex gap-4'>
        {brandTypes.map((item: BrandTypeCheckbox) => (
          <label key={item.name} className='flex items-center gap-2 mt-3 ml-2'>
            <Input
              type='radio'
              name='brandType'
              value={item.name}
              checked={item.checked}
              disabled
              className='form-radio'
            />
            {item.label}
          </label>
        ))}
      </fieldset>

      {activeBrand?.preorder && (
        <BrandDetailsCard
          data={{ label: 'Pre order duration', value: activeBrand?.preorder_duration_in_hours }}
          className='my-4'
        />
      )}
      {activeBrand.brand_type ? (
        <BrandDetailsCard
          data={{
            label: 'Brand Type',
            value: ALL_BRAND_TYPE_OPTIONS.find((o) => o.value === activeBrand.brand_type)?.text
          }}
          className='my-6'
        />
      ) : null}
      {activeBrand.cutoff_time ? (
        <BrandDetailsCard
          data={{
            label: 'Cutoff Time',
            value: moment(activeBrand?.cutoff_time, 'HH:mm').format('h:mm A')
          }}
          className='my-6'
        />
      ) : null}
      {isFCBBrand(activeBrand.brand_type) ? (
        <BrandDetailsCard
          data={{
            label: 'Companies',
            value: activeBrand.all_company_available
              ? 'All companies'
              : selectedCompanies.join(', ')
          }}
          className='my-6'
        />
      ) : null}
      <div className='my-8'>
        <BrandStatus
          isNew={activeBrand?.new}
          isTop={activeBrand?.top}
          popup={activeBrand?.pop_up}
          comingSoon={activeBrand?.coming_soon}
          setComingSoon={(k: boolean) => setActiveBrand({ ...activeBrand, coming_soon: k })}
          setNew={(k: boolean) => setActiveBrand({ ...activeBrand, new: k })}
          setPopup={(k: boolean) => setActiveBrand({ ...activeBrand, pop_up: k })}
          comment={activeBrand?.coming_soon_text}
          setComment={(k: string) => setActiveBrand({ ...activeBrand, coming_soon_text: k })}
          color={activeBrand?.popup_color}
          setColor={(k: string) => setActiveBrand({ ...activeBrand, popup_color: k })}
          setIsTop={(k: boolean) => setActiveBrand({ ...activeBrand, top: k })}
          className='my-6'
          disabled
        />
      </div>

      <BrandDetailsCard
        data={{
          label: 'Brand Active',
          tag: (
            <LiveTag
              live={activeBrand?.live}
              partnerSynced={activeBrand?.partner_synced}
              brandType={activeBrand?.brand_type}
            />
          ),
          value: null
        }}
        className='my-4'
      />

      <BrandImageDetailCard
        data={{ brandImage: activeBrand?.images?.[0], brandLogo: activeBrand?.logo }}
      />
    </div>
  );
};

export default PreviewBrandDetails;
